import { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebaseConfig';
import { doc, updateDoc, increment, onSnapshot } from 'firebase/firestore';
import useUnityAds from './useUnityAds';

// ─── Constants ───────────────────────────────────────────────────────────────
const DAILY_REWARDED_CAP = 15;       // Max rewarded ads per day (Tier 2 only)
const COOLDOWN_MS        = 120_000;  // 2-minute cooldown between rewarded ads
const REWARD_MIN         = 10;
const REWARD_MAX         = 25;

function randomReward() {
  return Math.floor(Math.random() * (REWARD_MAX - REWARD_MIN + 1)) + REWARD_MIN;
}

// ─── useAdManager ────────────────────────────────────────────────────────────
// Two-tier ad system:
//
//   Tier 1  showSilentAd(onDone)
//     → Interstitial ad placed at touchpoints (after game, after daily bonus)
//     → No Stream reward to user — revenue goes to app on impression
//     → Calls onDone() when dismissed or failed (never blocks the UX)
//
//   Tier 2  showRewardedAd()
//     → User-initiated rewarded video (Dashboard "Boost Earnings" button)
//     → Full watch  → 10–25 Streams credited, adsWatchedToday incremented
//     → Skip / fail → nothing, cooldown still applied
//     → Hard cap: 15 rewarded ads/day (tracked in Firestore)
//     → Cooldown: 2 minutes between rewarded ads
//
export function useAdManager() {
  const { ready, showAd, showInterstitial } = useUnityAds();

  const [adsToday,     setAdsToday]     = useState(0);
  const [lastAdTime,   setLastAdTime]   = useState(0);
  const [rewardState,  setRewardState]  = useState('idle'); // idle|loading|success|skipped|error
  const [lastReward,   setLastReward]   = useState(0);

  // Live-sync daily ad count from Firestore
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    return onSnapshot(doc(db, 'users', user.uid), snap => {
      if (snap.exists()) setAdsToday(snap.data().adsWatchedToday || 0);
    });
  }, []);

  // Reset daily count at midnight (client-side estimate; Firestore rule owns truth)
  useEffect(() => {
    const now  = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const msUntilMidnight = midnight.getTime() - now.getTime();
    const t = setTimeout(() => setAdsToday(0), msUntilMidnight);
    return () => clearTimeout(t);
  }, []);

  const cooldownLeft     = Math.max(0, COOLDOWN_MS - (Date.now() - lastAdTime));
  const adsRemaining     = Math.max(0, DAILY_REWARDED_CAP - adsToday);
  const canShowRewarded  = ready && adsRemaining > 0 && cooldownLeft === 0;

  // ── Tier 1: Silent interstitial ──────────────────────────────────────────
  const showSilentAd = useCallback((onDone) => {
    showInterstitial({
      onComplete: () => onDone?.(),
      onSkip:     () => onDone?.(),
      onFail:     () => onDone?.(),
    });
  }, [showInterstitial]);

  // ── Tier 2: Rewarded video ───────────────────────────────────────────────
  const showRewardedAd = useCallback(() => {
    if (!canShowRewarded) return;
    setRewardState('loading');

    const reward = randomReward();

    showAd({
      onReward: async () => {
        const user = auth.currentUser;
        if (user) {
          try {
            await updateDoc(doc(db, 'users', user.uid), {
              totalStreams:    increment(reward),
              adsWatchedToday: increment(1),
              interactions:   increment(1),
            });
          } catch (e) {
            console.error('[AdManager] Firestore error:', e);
          }
        }
        setLastReward(reward);
        setLastAdTime(Date.now());
        setRewardState('success');
        setTimeout(() => setRewardState('idle'), 3000);
      },
      onSkip: () => {
        setLastAdTime(Date.now());
        setRewardState('skipped');
        setTimeout(() => setRewardState('idle'), 2500);
      },
      onFail: (msg) => {
        console.warn('[AdManager] Rewarded ad failed:', msg);
        setRewardState('error');
        setTimeout(() => setRewardState('idle'), 2500);
      },
    });
  }, [canShowRewarded, showAd]);

  return {
    ready,
    canShowRewarded,
    adsToday,
    adsRemaining,
    cooldownLeft,
    rewardState,
    lastReward,
    showSilentAd,
    showRewardedAd,
  };
}
